import { Component, inject, OnInit, OnDestroy } from '@angular/core';
import { CommonModule } from '@angular/common';
import { RouterOutlet } from '@angular/router';
import { Subscription } from 'rxjs';
import { Router } from '@angular/router';
import { MatDialog, MatDialogModule } from '@angular/material/dialog';
import { SecureTokenStorageService } from './core/services/secure-token-storage.service';
import { InactivityMonitorService } from './core/services/inactivity-monitor.service';
import { ActivityHeartbeatService } from './core/services/activity-heartbeat.service';
import { AuthDavesaV2Service } from './core/auth-davesa/auth-davesa-v2.service';
import { InactivityWarningDialogComponent, InactivityWarningDialogData } from './shared/components/inactivity-warning-dialog/inactivity-warning-dialog.component';
import { InactivityDialogAction } from './core/constants/inactivity.constants';

@Component({
    selector: 'app-root',
    standalone: true,
    imports: [CommonModule, RouterOutlet, MatDialogModule],
    templateUrl: './app.component.html',
    styleUrl: './app.component.scss'
})
export class AppComponent implements OnInit, OnDestroy {
    title = 'davesa-app';
    
    private _secureTokenStorage = inject(SecureTokenStorageService);
    private _inactivityMonitor = inject(InactivityMonitorService);
    private _activityHeartbeat = inject(ActivityHeartbeatService);
    private _authService = inject(AuthDavesaV2Service);
    private _router = inject(Router);
    private _dialog = inject(MatDialog);
    
    private warningSubscription?: Subscription;
    private timeoutSubscription?: Subscription;
    private loginSubscription?: Subscription;
    private currentWarningDialog: any = null;

    ngOnInit() {
        // Subscribe to inactivity warning
        this.warningSubscription = this._inactivityMonitor.inactivityWarning$.subscribe(secondsRemaining => {
            if (secondsRemaining !== null) {
                this.showInactivityWarning(secondsRemaining);
            } else {
                // Warning was dismissed (user became active again)
                this.closeInactivityWarning();
            }
        });
        
        // Subscribe to inactivity timeout
        this.timeoutSubscription = this._inactivityMonitor.inactivityTimeout$.subscribe(timedOut => {
            if (timedOut) {
                this.handleInactivityTimeout();
            }
        });
        
        // Subscribe to login/logout events
        // This handles cases where user logs in after app initialization
        this.loginSubscription = this._secureTokenStorage.getUserDataObservable().subscribe(userData => {
            if (userData && !this._inactivityMonitor.isMonitoringActive()) {
                this._inactivityMonitor.startMonitoring();
                this._activityHeartbeat.startHeartbeat();
            } else if (!userData && this._inactivityMonitor.isMonitoringActive()) {
                this._inactivityMonitor.stopMonitoring();
                this._activityHeartbeat.stopHeartbeat();
            }
        });
        
        // Start monitoring if user is already logged in (for page refresh)
        if (this._authService.isLoggedIn()) {
            this._inactivityMonitor.startMonitoring();
            this._activityHeartbeat.startHeartbeat();
        }
    }
    
    ngOnDestroy() {
        this.warningSubscription?.unsubscribe();
        this.timeoutSubscription?.unsubscribe();
        this.loginSubscription?.unsubscribe();
        this.closeInactivityWarning();
    }
    
    /**
     * Show inactivity warning dialog
     */
    private showInactivityWarning(secondsRemaining: number): void {
        // If dialog is already open, don't open another one
        if (this.currentWarningDialog) {
            return;
        }

        const dialogData: InactivityWarningDialogData = {
            secondsRemaining
        };

        this.currentWarningDialog = this._dialog.open(InactivityWarningDialogComponent, {
            width: '500px',
            disableClose: true,
            data: dialogData
        });

        this.currentWarningDialog.afterClosed().subscribe((result: any) => {
            this.currentWarningDialog = null;

            if (result?.action === InactivityDialogAction.STAY) {
                this._inactivityMonitor.resetTimer();
            } else if (result?.action === InactivityDialogAction.LOGOUT) {
                // Broadcast logout to other tabs before executing
                this._inactivityMonitor.triggerLogout();
            }
        });
    }

    /**
     * Close inactivity warning dialog if open
     */
    private closeInactivityWarning(): void {
        if (this.currentWarningDialog) {
            this.currentWarningDialog.close();
            this.currentWarningDialog = null;
        }
    }
    
    /**
     * Handle inactivity timeout - logout user
     */
    private async handleInactivityTimeout() {
        try {
            await this._authService.signOutDueToInactivity();
            // Navigate to sign-in with reason parameter to show inactivity message
            this._router.navigate(['/sign-in'], {
                queryParams: { reason: 'inactivity' }
            });
        } catch (error) {
            console.error('[AppComponent] Error during inactivity logout:', error);
        }
    }
}
